// =============================================================================
// Copyright   : 2023 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : llm integration
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Item, Message, Button} from 'semantic-ui-react'
import now from 'performance-now'

// ---Framework imports
import log from 'uix/logging'
import {groupBy, values, strip, sum, size, sorted, uniq, mapItems, dict, isSet, isNil} from 'ldash'
import {
  useEffect,
  useGraphics,
  useMemo,
  useState,
  useCallback,
  useSelector,
  useInView,
  usePersistent,
} from 'uix/hooks'
import {Actions, Sel} from 'uix/registries'
import {AutoSizer, SplitPane, XMenu, PortalTarget, AutoPortal} from 'uix/components'
import {hashObj} from 'uix/utils'
import {TypeDescription} from 'uix/types/components'

import {ChatPanel} from 'uix/components/llm/chat'
import styles from 'uix/components/llm/chat.scss'

// ---Local imports
import {DataTable, buildCsvData} from './table'
import {useColumnsConfig} from './hooks'
import {useColumnContext} from './columnTypes'
import {getColumnDescriptionText} from './columns'

// ---Exports
const CsvItem = ({sortedData, internalColumns, setJsonData, groupBy}) => {
  let [ref, onScreen] = useInView({triggerOnce: false})
  let {columnLabel, columnNamespaceLabel} = useColumnContext()
  let i18n = useSelector(Sel.i18n)
  //~ log.x('111', groupBy)
  let json_data = useMemo(() => {
    if (!onScreen) return []

    let {csv_cols, csv_datas} = buildCsvData({
      sortedData,
      internalColumns,
      i18n,
      columnLabel,
      columnNamespaceLabel,
      groupBy,
    })
    let colid2label = dict(csv_cols.map(({id, displayName}) => [id, displayName]))
    return csv_datas.map(row => dict(mapItems(row, (k, v) => [colid2label[k], v])))
  }, [onScreen, size(sortedData), sortedData[0]])

  return (
    <AutoPortal target="TableControllerCsvItem">
      <XMenu.PopupItem content="Read Json into LLM" basic keepOpen>
        <span ref={ref} />
        <Button compact basic onClick={() => setJsonData(json_data.slice(0, 10))} content="10" />
        <Button compact basic onClick={() => setJsonData(json_data.slice(0, 100))} content="100" />
        <Button
          compact
          basic
          onClick={() => setJsonData(json_data)}
          content={`All ${sortedData?.length}`}
        />
        <Button compact basic onClick={() => setJsonData([])} content="None" />
        <Button compact basic onClick={() => vl.pprint(json_data.slice(0, 5))} content="Show" />
      </XMenu.PopupItem>
    </AutoPortal>
  )
}

const useDecodedTypes = (functionCall, args) => {
  // decode md types
  let s = args.selected_document_types || ''
  let split_ch = s.includes('|') ? '|' : ','
  let md_types = s.split(split_ch).filter(Boolean) || []
  if (functionCall && md_types.length == 0) {
    // a functionCall without types will default to all
    md_types = ['all_document_types']
  }
  md_types = md_types.map(strip)
  if (md_types.includes('all_document_types')) {
    if (md_types.length == 1) {
      md_types = ['_metadata_']
    } else {
      md_types = md_types.filter(t => t != 'all_document_types')
    }
  }

  let [known_types, set_known_types] = useState([])
  useEffect(() => {
    if (md_types?.length) set_known_types(md_types)
    // reset
    if (functionCall == null) set_known_types([])
  }, [hashObj(md_types)])
  return md_types.length ? md_types : known_types
}

const useDecodedColumns = (colcfg, functionCall, args, filtered, tblsorted) => {
  // decode columns

  return useMemo(() => {
    let cols = args.selected_columns?.split(',') || []
    cols = cols.map(strip)

    if (!args.group_by) {
      //XXX: do we need this anymore? only tcaption is known to LLM
      // insert tcaption if neither caption is available
      if (!cols.includes('caption') && !cols.includes('tcaption')) {
        cols = ['tcaption', ...cols]
      }
    }
    // remove caption if tcaption is available
    if (cols.includes('tcaption') && cols.includes('caption')) {
      cols = cols.filter(c => c != 'caption')
    }

    cols = [...cols, ...filtered.map(col => col.id).filter(c => !cols.includes(c))]
    cols = [...cols, ...tblsorted.map(col => col.id).filter(c => !cols.includes(c))]

    let badColumns = cols.filter(col => !colcfg[col])
    let columns = cols.filter(col => colcfg[col])
    return {columns, badColumns}
  }, [functionCall])
}

const useDecodedFilter = (colcfg, filter_by) =>
  useMemo(() => {
    if (!filter_by) return []
    let cols_values = filter_by.split(',')
    let res = cols_values.map(cv => {
      let colid = cv.split(':')[0]
      let value = cv
        .split(':')
        .slice(1)
        .join(':')
      return {id: colid, value}
    })
    // group by colids
    res = mapItems(groupBy(res, col => col.id), (colid, cols) => [colid, cols.map(c => c.value)])

    return res.map(([colid, vals]) => {
      let value = vals.length == 1 ? vals[0] : vals

      let constructFilter = colcfg[colid]?.constructFilter
      if (constructFilter) {
        value = constructFilter(value)
      }

      return {id: colid, value}
    })
  }, [filter_by])

const useDecodedSort = (sort_by, sort_order) =>
  useMemo(() => (sort_by ? [{id: sort_by, desc: sort_order == 'desc'}] : []), [sort_by, sort_order])

export const useLlmTableFunctionCallDecoding = ({colcfg, functionCall}) => {
  let args = functionCall?.arguments || {}

  let {
    limit: showMaxRecords,
    filter: filter_by,
    group_by,
    show_footer: showFooter,
    sort_by,
    sort_order = 'asc',
  } = args

  let tblsorted = useDecodedSort(sort_by, sort_order)
  let filtered = useDecodedFilter(colcfg, filter_by)
  let types = useDecodedTypes(functionCall, args)
  let {columns, badColumns} = useDecodedColumns(colcfg, functionCall, args, filtered, tblsorted)

  return {
    types,
    showMaxRecords,
    filtered,
    tblsorted,
    groupBy: group_by,
    showFooter,
    columns,
    raw: {sort_by, sort_order, filter_by},
    invalid: {badColumns},
  }
}

const ConfiguredTable = ({functionCall, setJsonTableData}) => {
  useGraphics(['types'])
  let colcfg = useColumnsConfig()
  let {
    types,
    showMaxRecords,
    filtered,
    tblsorted,
    groupBy,
    showFooter,
    columns,
    invalid,
    raw,
  } = useLlmTableFunctionCallDecoding({
    colcfg,
    functionCall,
  })

  let defaultPageSize = showMaxRecords || 20

  let tableStateComponents = useMemo(
    () => [props => <CsvItem {...props} setJsonData={setJsonTableData} groupBy={groupBy} />],
    [setJsonTableData, groupBy]
  )

  let content = null
  if (types.length > 0) {
    content = (
      <AutoSizer>
        {({height, width}) => (
          <div style={{height, width, display: 'flex', flexDirection: 'row'}}>
            <DataTable
              flex
              subdomains
              resolveLinks
              groupHeaders="auto"
              showPaginationTop
              limit={200000}
              types={types}
              key={hashObj(functionCall)}
              defaultColumns={columns}
              options={{selectable: false, groupBy, showFooter}}
              defaultSorted={tblsorted}
              defaultFiltered={filtered}
              defaultPageSize={defaultPageSize}
              minRows={0}
              tableStateComponents={tableStateComponents}
            />
          </div>
        )}
      </AutoSizer>
    )
  } else {
    content = <Message>Keine Typen ausgewählt</Message>
  }

  return (
    <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
      <XMenu attached size="small">
        <XMenu.PopupItem content={`Typen (${types.length})`} basic padded>
          {types.map(n => (
            <TypeDescription as="div" key={n} name={n} />
          ))}
        </XMenu.PopupItem>
        <XMenu.PopupItem content={`Spalten (${columns.length})`} basic padded>
          {columns.map(n => (
            <div key={n}>{n}</div>
          ))}
          {invalid.badColumns.map(n => (
            <div style={{color: 'red'}} key={n}>
              {n}
            </div>
          ))}
        </XMenu.PopupItem>
        {showMaxRecords && <XMenu.Item content={`Limit: ${showMaxRecords}`} />}
        {raw.sort_by && <XMenu.Item content={`Sort: ${raw.sort_by}/${raw.sort_order}`} />}
        {groupBy && <XMenu.Item content={`Group: ${groupBy}`} />}
        {raw.filter_by && (
          <XMenu.PopupItem content={`Filter (${filtered.length})`} basic padded>
            {filtered.map((col, i) => (
              <div key={i}>
                {col.id}: {isSet(col.value) ? [...col.value].join(', ') : col.value}
              </div>
            ))}
            <div>decoded from: {raw.filter_by}</div>
          </XMenu.PopupItem>
        )}
        {!isNil(showFooter) && <XMenu.Item content={`Footer: ${showFooter}`} />}
        <XMenu.BeginRight />
        <PortalTarget id="TableControllerCsvItem" as="div" />
      </XMenu>
      <div style={{flex: 1}}>{content}</div>
    </div>
  )
}

export const TableController = ({}) => {
  let [jsonTableData, setJsonTableData] = usePersistent('table_jsondata', [])

  let colcfg = useColumnsConfig()

  let contextRules = useMemo(() => {
    let exclude = ['caption']
    return {
      table_func: {
        buildData: () => ({selected_columns: getColumnDescriptionText(colcfg, exclude)}),
        label: 'Table-Support',
        buildChatIndicator: () => null,
      },
      custom_json_data: {
        buildData: () => ({custom_json_data: jsonTableData}),
        label: `Inject-Json (${size(jsonTableData)})`,
        buildChatIndicator: () =>
          size(jsonTableData) ? (
            <span className={styles.contextIndicator}>
              <span onClick={() => vl.pprint(jsonTableData)}>{`Inject-Json (${size(
                jsonTableData
              )}) `}</span>
              <Button
                basic
                compact
                style={{padding: 0, verticalAlign: 'text-top'}}
                icon="delete"
                onClick={() => setJsonTableData([])}
                title="Remove"
                size="mini"
              />
            </span>
          ) : null,
        //~ onNewChat: () => setJsonTableData([]),
      },
    }
  }, [colcfg, jsonTableData])

  let [functionCall, setFunctionCall] = useState(null)
  return (
    <SplitPane vertical flexInner>
      <div style={{flex: 1}}>
        <ChatPanel onFunctionCall={setFunctionCall} contextRules={contextRules} />
      </div>
      <ConfiguredTable functionCall={functionCall} setJsonTableData={setJsonTableData} />
    </SplitPane>
  )
}
